import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { config } from "../../config";

interface Organization {
  id: string;
  name: string;
  slug: string;
  subdomain?: string;
  createdAt: string;
  settings: {
    ssoOnly: boolean;
    allowGoogle: boolean;
    allowMicrosoft: boolean;
    allowPassword: boolean;
    mfaRequired: boolean;
  };
}

export function OrganizationsPage() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchOrganizations = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${config.apiBaseUrl}/v1/admin/organizations`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch organizations");
      }

      const data = await response.json();
      setOrganizations(data.organizations ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const filteredOrgs = organizations.filter(
    (org) =>
      org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      org.slug.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Organizations</h1>
        <Link to="/admin/organizations/new" className="btn btn-primary">
          Create Organization
        </Link>
      </header>

      <div className="admin-toolbar">
        <input
          type="text"
          placeholder="Search organizations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {isLoading && (
        <div className="admin-loading">
          <div className="loading-spinner" />
          <p>Loading organizations...</p>
        </div>
      )}

      {error && (
        <div className="admin-error">
          <p>{error}</p>
          <button type="button" onClick={fetchOrganizations}>
            Retry
          </button>
        </div>
      )}

      {!isLoading && !error && filteredOrgs.length === 0 && (
        <div className="admin-empty">
          <p>No organizations found.</p>
          <Link to="/admin/organizations/new" className="btn btn-primary">
            Create your first organization
          </Link>
        </div>
      )}

      {!isLoading && !error && filteredOrgs.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Slug</th>
              <th>Subdomain</th>
              <th>Auth Methods</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrgs.map((org) => (
              <tr key={org.id}>
                <td>
                  <Link to={`/admin/organizations/${org.id}`}>{org.name}</Link>
                </td>
                <td>{org.slug}</td>
                <td>{org.subdomain ?? "-"}</td>
                <td>
                  <div className="auth-badges">
                    {org.settings.allowPassword && <span className="badge">Password</span>}
                    {org.settings.allowGoogle && <span className="badge">Google</span>}
                    {org.settings.allowMicrosoft && <span className="badge">Microsoft</span>}
                    {org.settings.ssoOnly && <span className="badge badge-warning">SSO Only</span>}
                  </div>
                </td>
                <td>{new Date(org.createdAt).toLocaleDateString()}</td>
                <td>
                  <Link to={`/admin/organizations/${org.id}`} className="btn-link">
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
